import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../common/Icons';
import { HEAD_DOCTOR_PATHS } from '../../doctor/doctorPaths';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';

const H = HEAD_DOCTOR_COPY.DASHBOARD;

function fmt(n) {
  return Number(n || 0).toLocaleString('fr-FR');
}

function RankList({
  title,
  hint,
  icon,
  tone = 'indigo',
  items,
  loading,
  emptyLabel,
  seeAllTo,
  seeAllLabel
}) {
  const max = Math.max(...items.map((i) => Number(i.value || 0)), 1);

  return (
    <article className={`hd-dash-rank-card hd-dash-rank-card--${tone}`}>
      <header className="hd-dash-rank-card__head">
        <div className="hd-dash-rank-card__head-main">
          <span className="hd-dash-rank-card__icon" aria-hidden>
            <Icon name={icon} size={16} />
          </span>
          <div>
            <h3 className="hd-dash-rank-card__title">{title}</h3>
            {hint ? <p className="hd-dash-rank-card__hint mb-0">{hint}</p> : null}
          </div>
        </div>
        {seeAllTo ? (
          <Link to={seeAllTo} className="hd-dash-rank-card__more">
            {seeAllLabel}
            <Icon name="chevronRight" size={14} aria-hidden />
          </Link>
        ) : null}
      </header>

      {loading ? (
        <div className="hd-dash-rank-card__empty">
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
          {HEAD_DOCTOR_COPY.REPORTS.LOADING}
        </div>
      ) : items.length === 0 ? (
        <div className="hd-dash-rank-card__empty">{emptyLabel}</div>
      ) : (
        <ol className="hd-dash-rank-list">
          {items.map((item, idx) => {
            const pct = Math.round((Number(item.value || 0) / max) * 100);
            return (
              <li key={item.id || `${item.label}-${idx}`} className="hd-dash-rank-list__item">
                <span className="hd-dash-rank-list__pos">{idx + 1}</span>
                <div className="hd-dash-rank-list__body">
                  <div className="hd-dash-rank-list__row">
                    <span className="hd-dash-rank-list__label" title={item.label}>
                      {item.code ? <code className="hd-dash-rank-list__code">{item.code}</code> : null}
                      {item.label}
                    </span>
                    <strong className="hd-dash-rank-list__value">{fmt(item.value)}</strong>
                  </div>
                  {item.meta ? <span className="hd-dash-rank-list__meta">{item.meta}</span> : null}
                  <div className="hd-dash-rank-list__track" aria-hidden>
                    <span className="hd-dash-rank-list__fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </article>
  );
}

export default function DashboardHighlights({
  pathologies = [],
  medications = [],
  practitioners = [],
  loading = false
}) {
  const pathoItems = pathologies.slice(0, 5).map((row) => ({
    id: row.disease_id,
    code: row.disease_code,
    label: row.disease_name || '—',
    value: Number(row.consultation_count || row.occurrence_count || 0),
    meta: H.TOP_PATHO_META(Number(row.consultation_count || 0))
  }));

  const medItems = medications.slice(0, 5).map((row) => ({
    id: row.medication_id,
    label: row.medication_name || '—',
    value: Number(row.total_quantity || 0),
    meta: H.TOP_MED_META(Number(row.prescription_count || 0), row.unit_name)
  }));

  const doctorItems = practitioners.slice(0, 5).map((row) => ({
    id: row.doctor_id || row.user_id || row.practitioner_id,
    label: row.doctor_name || row.practitioner_name || '—',
    value: Number(row.consultation_count || row.prescription_count || 0),
    meta: row.practitioner_role ? H.TOP_DOCTOR_ROLE(row.practitioner_role) : null
  }));

  return (
    <section className="hd-dash-highlights mb-4" aria-label={H.HIGHLIGHTS_TITLE}>
      <div className="mb-3">
        <h2 className="hd-dash-primary__title mb-1">{H.HIGHLIGHTS_TITLE}</h2>
        <p className="hd-dash-primary__desc mb-0">{H.HIGHLIGHTS_SUBTITLE}</p>
      </div>
      <div className="hd-dash-highlights__grid">
        <RankList
          title={H.TOP_PATHO_TITLE}
          hint={H.TOP_PATHO_HINT}
          icon="alertCircle"
          tone="amber"
          items={pathoItems}
          loading={loading}
          emptyLabel={H.TOP_PATHO_EMPTY}
          seeAllTo={HEAD_DOCTOR_PATHS.STATISTIQUES_PATHOLOGIES}
          seeAllLabel={H.TOP_SEE_ALL}
        />
        <RankList
          title={H.TOP_MED_TITLE}
          hint={H.TOP_MED_HINT}
          icon="pills"
          tone="teal"
          items={medItems}
          loading={loading}
          emptyLabel={H.TOP_MED_EMPTY}
          seeAllTo={HEAD_DOCTOR_PATHS.STATISTIQUES_MEDICAMENTS}
          seeAllLabel={H.TOP_SEE_ALL}
        />
        <RankList
          title={H.TOP_DOCTOR_TITLE}
          hint={H.TOP_DOCTOR_HINT}
          icon="user"
          tone="indigo"
          items={doctorItems}
          loading={loading}
          emptyLabel={H.TOP_DOCTOR_EMPTY}
          seeAllTo={HEAD_DOCTOR_PATHS.STATISTIQUES_CONSULTATIONS}
          seeAllLabel={H.TOP_SEE_ALL}
        />
      </div>
    </section>
  );
}
